// Sous-ensemble typé de l'API Google Maps JavaScript utilisé par RECA (Balises terrain, Routes).
// Pas de dépendance @types/google.maps : le chargement est à la demande et typé ici.

export interface LatLngLiteral {
  lat: number
  lng: number
}

export interface PixelPoint {
  x: number
  y: number
}

export interface MapProjection {
  fromLatLngToDivPixel: (position: LatLngLiteral) => PixelPoint | null
  /** Inverse : point de la couche (pixels) vers coordonnées (mode ajustement de l'écran Routes). */
  fromDivPixelToLatLng: (pixel: PixelPoint) => { lat: () => number; lng: () => number } | null
}

export interface MapBounds {
  contains: (position: LatLngLiteral) => boolean
}

export interface MapListener {
  remove: () => void
}

export interface GoogleMapInstance {
  setMapTypeId: (id: 'satellite' | 'roadmap') => void
  /** Rotation native : effective seulement sur une carte vectorielle (Map ID). */
  setHeading: (heading: number) => void
  getHeading: () => number | undefined
  getRenderingType: () => 'VECTOR' | 'RASTER' | 'UNINITIALIZED'
  setOptions: (options: Record<string, unknown>) => void
  getZoom: () => number | undefined
  setZoom: (zoom: number) => void
  panTo: (position: LatLngLiteral) => void
  setCenter: (position: LatLngLiteral) => void
  fitBounds: (bounds: { north: number; south: number; east: number; west: number }, padding?: number) => void
  getBounds: () => MapBounds | undefined
  addListener: (event: string, handler: () => void) => MapListener
  /** Centre courant (recentrage par marges). */
  getCenter: () => { lat: () => number; lng: () => number } | undefined
  /** Inclinaison native (carte vectorielle seulement; sans effet en matriciel). */
  setTilt: (tilt: number) => void
  getTilt: () => number | undefined
}

// Méthodes (et non propriétés) : les sous-classes surchargent onAdd/draw/onRemove.
export interface OverlayViewBase {
  setMap(map: GoogleMapInstance | null): void
  getPanes(): { overlayMouseTarget: HTMLElement } | null
  getProjection(): MapProjection | null
  onAdd(): void
  draw(): void
  onRemove(): void
}

export interface MapPolyline {
  setMap: (map: GoogleMapInstance | null) => void
  setOptions: (options: Record<string, unknown>) => void
}

export interface StreetViewPanorama {
  setPano: (pano: string) => void
  setPov: (pov: { heading: number; pitch: number }) => void
  setVisible: (visible: boolean) => void
}

export interface StreetViewLocation {
  pano?: string
  latLng?: { lat: () => number; lng: () => number }
}

export interface GoogleMapsApi {
  /** Vue de rue interactive dans son propre élément (jamais dans le cadre qui tourne). */
  StreetViewPanorama: new (element: HTMLElement, options: Record<string, unknown>) => StreetViewPanorama
  /** `getPanorama` rejette la promesse quand aucune vue n'existe dans le rayon demandé. */
  StreetViewService: new () => {
    getPanorama: (request: Record<string, unknown>) => Promise<{ data: { location?: StreetViewLocation } }>
  }
  StreetViewSource: { OUTDOOR: string }
  StreetViewPreference: { NEAREST: string }
  Polyline: new (options: Record<string, unknown>) => MapPolyline
  Point: new (x: number, y: number) => PixelPoint
  /** Symboles prédéfinis (flèches de sens sur une polyligne). */
  SymbolPath: { FORWARD_CLOSED_ARROW: number }
  Map: new (element: HTMLElement, options: Record<string, unknown>) => GoogleMapInstance
  OverlayView: (new () => OverlayViewBase) & {
    /** Empêche la carte de traiter clics et gestes (glisser, pincer) faits sur cet élément. */
    preventMapHitsAndGesturesFrom?: (element: Element) => void
  }
  event: { trigger: (instance: unknown, event: string) => void }
}

declare global {
  interface Window {
    google?: { maps?: GoogleMapsApi }
  }
}

const scriptId = 'reca-google-maps-js'
let loading: Promise<GoogleMapsApi> | null = null

/** Charge l'API une seule fois ; un échec libère le verrou pour permettre « Recharger la carte ». */
export function loadGoogleMaps(apiKey: string): Promise<GoogleMapsApi> {
  const ready = window.google?.maps
  if (ready?.Map) return Promise.resolve(ready)
  if (loading) return loading
  loading = new Promise<GoogleMapsApi>((resolve, reject) => {
    document.getElementById(scriptId)?.remove()
    const script = document.createElement('script')
    script.id = scriptId
    script.async = true
    script.src = `https://maps.googleapis.com/maps/api/js?${new URLSearchParams({
      key: apiKey,
      v: 'weekly',
      language: 'fr',
      region: 'CA',
    }).toString()}`
    script.onload = () => {
      const maps = window.google?.maps
      if (maps?.Map) resolve(maps)
      else reject(new Error('GOOGLE_MAPS_UNAVAILABLE'))
    }
    script.onerror = () => {
      reject(new Error('GOOGLE_MAPS_LOAD_FAILED'))
    }
    document.head.append(script)
  }).catch((error: unknown) => {
    loading = null
    throw error instanceof Error ? error : new Error('GOOGLE_MAPS_LOAD_FAILED')
  })
  return loading
}
